import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PermissionAPI } from '../services/api';
import { formatTimeLeft } from '../utils/time';
import DocumentList from '../components/document/DocumentList';
import FolderManager from '../components/document/FolderManager';
import FileUploader from '../components/document/FileUploader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './DashboardPage.css';

interface Document {
  documentId: string;
  name: string;
  fileSize: number;
  pageCount: number;
  createdAt: string;
  folderId?: string;
}

interface SharedDocument {
  document_id: string;
  name: string;
  file_size: number;
  page_count: number;
  created_at: string;
  shared_by: string;
  shared_by_name?: string | null;
  shared_by_email?: string | null;
  shared_at: string;
  expires_at?: string | null;
  permissions: {
    can_view: boolean;
    can_download: boolean;
    can_print: boolean;
    watermark: boolean;
  };
}

const DashboardPage: React.FC = () => {
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>();
  const [currentFolderName, setCurrentFolderName] = useState<string | undefined>();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [docRefreshKey, setDocRefreshKey] = useState(0);
  const [sharedDocs, setSharedDocs] = useState<SharedDocument[]>([]);
  const [sharedDocsLoading, setSharedDocsLoading] = useState(false);
  const [sharedDocsError, setSharedDocsError] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    setSharedDocsLoading(true);
    setSharedDocsError('');
    PermissionAPI.getSharedWithMe()
      .then((data: any) => setSharedDocs(data?.documents ?? []))
      .catch(() => setSharedDocsError('Failed to load shared documents'))
      .finally(() => setSharedDocsLoading(false));
  }, [user]);

  const handleDocumentSelect = (document: Document) => {
    navigate(`/documents/${document.documentId}`);
  };

  const handleUploadSuccess = () => {
    setDocRefreshKey(k => k + 1);
  };

  const getCurrentFolderName = () => currentFolderName ?? 'All Documents';

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!user) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <div className="page-container">
      <div className="dashboard-layout">
        {/* Sidebar with Folder Manager */}
        <div className="dashboard-sidebar">
          <FolderManager
            currentFolderId={currentFolderId}
            onFolderChange={(folderId, folderName) => {
              setCurrentFolderId(folderId);
              setCurrentFolderName(folderName);
            }}
            onFolderCreated={() => {}}
          />
        </div>

        {/* Main Content Area */}
        <div className="dashboard-main">
          {/* Breadcrumb and Controls */}
          <div className="dashboard-controls">
            <div className="breadcrumb-section">
              <nav className="breadcrumb-nav">
                <button
                  className="breadcrumb-button"
                  onClick={() => { setCurrentFolderId(undefined); setCurrentFolderName(undefined); }}
                >
                  🏠 All Documents
                </button>
                {currentFolderId && (
                  <>
                    <span className="breadcrumb-separator"> / </span>
                    <span className="breadcrumb-current">{getCurrentFolderName()}</span>
                  </>
                )}
              </nav>
            </div>

            <div className="view-controls">
              <div className="view-mode-toggle">
                <button
                  className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                >
                  ⊞
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('list')}
                  title="List view"
                >
                  ☰
                </button>
              </div>
            </div>
          </div>

          {/* Inline Upload */}
          <FileUploader
            folderId={currentFolderId}
            onUploadSuccess={handleUploadSuccess}
            showProgress={true}
            compact
          />

          {/* Document List */}
          <DocumentList
            currentFolderId={currentFolderId}
            onDocumentSelect={handleDocumentSelect}
            viewMode={viewMode}
            showActions={true}
            refreshKey={docRefreshKey}
          />

          {/* Shared With Me */}
          <div className="shared-with-me-section">
            <h5 className="section-title">🔗 Shared with Me{sharedDocs.length > 0 && ` (${sharedDocs.length})`}</h5>
            {sharedDocsLoading ? (
              <LoadingSpinner size="small" message="Loading shared documents..." />
            ) : sharedDocsError ? (
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>{sharedDocsError}</p>
            ) : sharedDocs.length === 0 ? (
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>No documents have been shared with you yet.</p>
            ) : (
              <div className={`items-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
                {sharedDocs.map(doc => (
                  <div
                    key={doc.document_id}
                    className="item-card document-card shared-doc-card"
                    onClick={() => navigate(`/documents/${doc.document_id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="item-content">
                      <div className="item-icon">📄</div>
                      <div className="item-details">
                        <h6 className="item-name" title={doc.name}>{doc.name}</h6>
                        <div className="item-meta">
                          <small className="text-muted">
                            {formatFileSize(doc.file_size)} · {doc.page_count} pages
                          </small>
                          <br />
                          <small className="text-muted">
                            Shared by {doc.shared_by_name || doc.shared_by_email || doc.shared_by} · {new Date(doc.shared_at).toLocaleDateString()}
                          </small>
                          <br />
                          <small className="shared-perms">
                            {doc.permissions.can_view && <span className="perm-tag">View</span>}
                            {doc.permissions.can_download && <span className="perm-tag">Download</span>}
                            {doc.permissions.can_print && <span className="perm-tag">Print</span>}
                            {(() => {
                              const timeLeft = formatTimeLeft(doc.expires_at);
                              if (!timeLeft) return null;
                              return (
                                <span className={`perm-tag expiry-tag${timeLeft === 'Expired' ? ' expiry-expired' : ''}`}>
                                  {timeLeft}
                                </span>
                              );
                            })()}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="item-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={e => { e.stopPropagation(); navigate(`/documents/${doc.document_id}`); }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>


    </div>
  );
};

export default DashboardPage;